import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Layout, Menu, MenuProps } from "antd";
import { DashbordRoute } from "../../routes/adminRoutes";
import Profile from "./ProfileDropdown";
import { confirmTick, logo } from "../../assets";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import "./dashboard.css";
import HeaderSearch from "./HeaderSearch";
import { setHeaderSearchTitle } from "../../redux/apiSlice/commonSlice";
import { getAuthState, getExamState } from "../../redux/store/getState";
import axiosClient from "../../api/axiosClient";
import handleApiError from "../../utils/handleError";
import ExamCountDownTimer from "../mySession/ExamCountDownTimer";
import successModal from "../../components/ConfirmationModal";
import { finishExam } from "../../utils/finishExam";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";

const { Header, Content, Sider } = Layout;
type Props = {
  routes: DashbordRoute[];
};

const Dashboard: React.FC<Props> = ({ routes }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  const [collapsed, setCollapsed] = useState(false);
  const [resettingLab, setResettingLab] = useState(false);

  const { user, homeRoute } = useAppSelector(getAuthState);

  const { isExamRunning, examTimeLeft, examLabAvailable } =
    useAppSelector(getExamState);

  const activeLink = routes.find(({ path }: DashbordRoute) => {
    return pathname.split("/")[1] === path;
  });

  useEffect(() => {
    if (!activeLink) {
      navigate(homeRoute);
    } else {
      dispatch(
        setHeaderSearchTitle({
          title: activeLink?.label?.toLowerCase(),
          value: "",
        })
      );
    }
    // eslint-disable-next-line
  }, [activeLink, dispatch, homeRoute]);

  const hasPermission = (permission?: string) =>
    user?.permissions?.includes("admin") ||
    (permission && user?.permissions?.includes(permission));

  /* Detail pages (/voucher-management/:id) are hidden routes of their own, so
     match on the first path segment to keep the parent item lit while you're
     inside one. */
  const selectedKey = routes.find(
    ({ hidden, path }) => !hidden && path.split("/")[0] === pathname.split("/")[1]
  )?.key;

  /* Flat route list -> sectioned menu. An item starts a new section when it
     declares a `group`; everything after it falls under that heading until the
     next one. Routes with no groups at all render as a plain list. */
  const menuItems = (): MenuProps["items"] => {
    const items: NonNullable<MenuProps["items"]> = [];

    routes
      .filter(
        ({ hidden, permission }) =>
          !hidden && (!permission || hasPermission(permission))
      )
      .forEach(({ Icon, path, key, label, group }) => {
        const item = {
          key,
          icon: <Icon />,
          label,
          onClick: () => navigate(`/${path}`),
        };

        const last = items[items.length - 1] as
          | { type?: string; children?: unknown[] }
          | undefined;

        if (group) {
          items.push({
            type: "group",
            key: `group-${group}`,
            label: group,
            children: [item],
          });
        } else if (last?.type === "group") {
          last.children?.push(item);
        } else {
          items.push(item);
        }
      });

    return items;
  };

  const params = useParams();

  async function endTest() {
    try {
      await axiosClient.post(`examinstances/${params.id}/submitexam`);
      finishExam("Congratulation! Your exam is ended.", () =>
        navigate("/my-session", { replace: true })
      );
    } catch (error) {
      handleApiError(error);
    }
  }
  async function resetLab() {
    /* A reset terminates and re-launches the candidate's VMs, so a double-click
       must not fire it twice — the button stays disabled until we hear back. */
    if (resettingLab) return;
    setResettingLab(true);
    try {
      await axiosClient.post(`examinstances/${params.id}/lab/reset`);
      successModal.show({
        image: confirmTick,
        text: "Lab reset has started. This may take a few minutes to complete.",
      });
    } catch (error) {
      handleApiError(error);
    } finally {
      setResettingLab(false);
    }
  }
  async function downloadVPNConfig() {
    try {
      const res = await axiosClient.post(
        `examinstances/${params.id}/lab/vpnconfig`,
        {},
        {
          responseType: "blob", // This is crucial for file downloads
        }
      );

      // Create a Blob from the response data
      const blob = new Blob([res.data], { type: "application/octet-stream" });

      // Create a temporary link to trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "openvpn.conf"; // Set the file name

      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      /* responseType "blob" applies to error bodies too, so the API's own
         message arrives as a Blob and never reaches handleApiError. Read it
         back into the shape the handler expects. */
      const body = error?.response?.data;
      if (body instanceof Blob) {
        try {
          error.response.data = JSON.parse(await body.text());
        } catch {
          /* not JSON — leave it and fall back to the axios message */
        }
      }
      handleApiError(error);
    }
  }

  return (
    <Layout className="dashboard_container">
      <Header className="dashboard_header">
        {!isExamRunning ? (
          <Button
            className="sider-toggle"
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
        ) : null}
        <img className="header-logo" src={logo} alt="logo" />
        {activeLink?.search ? (
          <div className="header-search-container">
            <HeaderSearch />
          </div>
        ) : null}

        {isExamRunning ? (
          <div className="exam-header-controls">
            <div className="time-left-container">
              <ExamCountDownTimer initialTimeStamp={examTimeLeft} />
            </div>
            <div className="exam-header-buttons">
              {/* Lab controls only exist for papers that ship a lab — on a
                  flag-only paper there are no VMs behind them, so they could
                  only ever fail. */}
              {examLabAvailable ? (
                <>
                  <Button onClick={resetLab} loading={resettingLab}>
                    Reset Lab
                  </Button>
                  <Button onClick={downloadVPNConfig}>
                    Download VPN Config
                  </Button>
                </>
              ) : null}
              <Button type="primary" onClick={endTest}>
                End Test
              </Button>
            </div>
          </div>
        ) : (
          <div className="header-profile">
            <Profile />
          </div>
        )}
      </Header>
      <Layout>
        {!isExamRunning ? (
          <Sider
            className="dashboard_sider"
            width={244}
            collapsible={true}
            trigger={null}
            collapsed={collapsed}
          >
            <Menu
              mode="inline"
              inlineIndent={16}
              style={{ border: "none" }}
              selectedKeys={selectedKey ? [selectedKey] : []}
              items={menuItems()}
            />
          </Sider>
        ) : null}
        <Layout>
          <Content>
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default Dashboard;
